import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Typography,
  ControllerSelect,
  ControllerMenuItem,
  ControllerModal,
  DialogActions,
  DialogContent,
  ControllerAutocomplete,
  Stack,
  Box,
  CircularProgress,
} from '@bymason-private/legacy-pattern-components';
import { compareSemvers } from '@bymason/utils';
import {
  ArtifactType,
  BuildResult,
  BuildStatus,
  GroupModel,
  useCreateArtifactDeployment,
  useGetArtifacts,
  useGetJobsStatus,
  ArtifactDeployment,
  useGetHardware,
  ArtifactModel,
} from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import { FileUploadButton } from './upload-button';
import { MASON_OS_DEPLOY_NAME, DEPLOY_TYPE_MAP } from '../../../app-constants';
import { useToast } from '../../../providers';

interface DeployToGroupModalProps {
  group: Pick<GroupModel, 'name' | 'deviceFamily' | 'apiLevel'>;
  deployType: ArtifactType;
  minVersion?: string;
  open: boolean;
  controllerBuildEnabled?: boolean;
  onClose: () => void;
  onSuccess?: (deploy: ArtifactDeployment) => void;
  onCreateNewProject?: () => void;
}

export function DeployToGroupModal({
  open,
  group,
  deployType,
  minVersion,
  controllerBuildEnabled,
  onClose,
  onSuccess,
  onCreateNewProject,
}: DeployToGroupModalProps) {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [artifactName, setArtifactName] = useState<string | null>(
    deployType === ArtifactType.Ota ? MASON_OS_DEPLOY_NAME : null
  );
  const [artifactVersion, setArtifactVersion] = useState<string>('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [getHardware, hardware] = useGetHardware(authToken);
  const [getArtifacts, artifacts] = useGetArtifacts(authToken);
  const [getBuilds, builds] = useGetJobsStatus(authToken);
  const [createDeploy, deployResult, deployError, deployBusy] = useCreateArtifactDeployment(authToken);

  const retreiveArtifacts = useCallback(() => {
    if (customerId) {
      switch (deployType) {
        case ArtifactType.Apk:
          getArtifacts({
            customerId,
            type: deployType,
          });
          break;
        case ArtifactType.Config:
          getBuilds({
            customerId,
          });
          break;
        case ArtifactType.Ota:
          getArtifacts({
            customerId: 'mason',
            type: deployType,
          });
          break;
      }
    }
  }, [customerId, deployType, getArtifacts, getBuilds]);

  const handleArtifactNameChange = (evt: unknown, selected: string | null) => {
    setArtifactName(selected);
  };

  const handleArtifactVersionChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    setArtifactVersion(evt.target.value);
  };

  const handleDeployArtifact = useCallback(() => {
    if (customerId && artifactName && artifactVersion && deployType) {
      createDeploy({
        customer: customerId,
        group: group.name,
        name: artifactName,
        version: artifactVersion,
        type: deployType,
        push: true,
      });
    }
  }, [artifactName, artifactVersion, createDeploy, customerId, deployType, group.name]);

  const handleArtifactUpload = useCallback(
    (artifact: ArtifactModel) => {
      retreiveArtifacts();
      setToastSeverity('success');
      setToastMessage(
        `${artifact.name} v${artifact.version} successfully registered and ready for deployment`
      );
      setArtifactName(artifact.name);
      setArtifactVersion(artifact.version);
    },
    [retreiveArtifacts, setToastMessage, setToastSeverity]
  );

  const handleArtifactUploadError = useCallback(
    (error: string) => {
      setUploadError(`Unable to upload new ${deployType}: ${error}`);
    },
    [deployType]
  );

  const handleArtifactUploadBusy = useCallback((isBusy: boolean) => {
    setUploadBusy(isBusy);
  }, []);

  const filteredBuilds = useMemo(() => {
    return builds?.data.filter((build) => {
      return group.deviceFamily
        ? build.device === group.deviceFamily &&
            build.status === BuildStatus.Completed &&
            build.result === BuildResult.Success
        : builds?.data;
    });
  }, [builds?.data, group.deviceFamily]);

  const nameOptions = useMemo(() => {
    let nameSet: Set<string> = new Set();
    if (artifacts) {
      if (deployType === ArtifactType.Ota) {
        nameSet = new Set([MASON_OS_DEPLOY_NAME]);
      } else {
        nameSet = group.deviceFamily
          ? artifacts.reduce<Set<string>>((acc, art) => acc.add(art.name), new Set<string>())
          : new Set<string>();
      }
    } else if (filteredBuilds) {
      nameSet = filteredBuilds.reduce<Set<string>>((acc, build) => acc.add(build.project), new Set());
    }
    return [...nameSet];
  }, [artifacts, deployType, group.deviceFamily, filteredBuilds]);

  const versionOptions = useMemo(() => {
    let verSet: Set<string> = new Set();
    if (artifacts) {
      if (deployType === ArtifactType.Ota) {
        const osMajVersion = getOsFromApiLevel(group.apiLevel);

        // Sort and take the first match to get the the highest maxMasonVersion for that familyName/apiLevel
        // NOTE: Even though deployable ota versions are model specific, we only have familyName specificity
        // on the groups page at this time.
        const hardwareInfo = hardware?.devices
          .sort((devA, devB) => compareSemvers(devB.maxMasonVersion, devA.maxMasonVersion))
          .find(
            (dev) => dev.familyName === group.deviceFamily && dev.maxMasonVersion.startsWith(osMajVersion || '')
          );
        verSet = artifacts
          .filter((art) => {
            return (
              art.type === deployType &&
              art.name === artifactName &&
              art.releaseVersion &&
              art.releaseVersion.startsWith(osMajVersion || '') &&
              compareSemvers(art.releaseVersion, minVersion || '') >= 0 &&
              compareSemvers(art.releaseVersion, hardwareInfo?.maxMasonVersion || '') <= 0
            );
          })
          // releaseVersion has been filtered to be present
          .sort((artA, artB) => compareSemvers(artB.releaseVersion || '', artA.releaseVersion || ''))
          .reduce<Set<string>>(
            (acc, art) => (art.releaseVersion ? acc.add(art.releaseVersion) : acc),
            new Set()
          );
      } else if (deployType === ArtifactType.Apk) {
        verSet = artifacts
          .filter((art) => art.type === deployType && art.name === artifactName)
          .sort((artA, artB) => artB.createdAt.localeCompare(artA.createdAt))
          .reduce<Set<string>>((acc, art) => acc.add(art.version), new Set());
      }
    } else if (filteredBuilds) {
      verSet = filteredBuilds
        .filter((build) => build.project === artifactName)
        .sort((buildA, buildB) => buildB.createdAt.localeCompare(buildA.createdAt))
        .reduce<Set<string>>((acc, build) => acc.add(build.configVersion), new Set());
    }
    return [...verSet];
  }, [
    artifacts,
    filteredBuilds,
    deployType,
    group.apiLevel,
    group.deviceFamily,
    artifactName,
    minVersion,
    hardware,
  ]);

  const isValidDeploy = useMemo(() => {
    return Boolean(customerId && artifactName && artifactVersion && deployType);
  }, [artifactName, artifactVersion, customerId, deployType]);

  useEffect(() => {
    getHardware({});
  }, [getHardware]);

  useEffect(() => {
    retreiveArtifacts();
  }, [retreiveArtifacts]);

  useEffect(() => {
    if (!deployBusy && !deployError && deployResult) {
      onSuccess && onSuccess(deployResult.data);
      onClose();
    }
  }, [deployBusy, deployError, deployResult, onClose, onSuccess]);

  useEffect(() => {
    if (uploadError) {
      setToastSeverity('error');
      setToastMessage(uploadError);
      setUploadError('');
    }
  }, [setToastMessage, setToastSeverity, uploadError]);

  return (
    <ControllerModal onClose={onClose} open={open} title={`Deploy ${DEPLOY_TYPE_MAP.get(deployType)}`}>
      <DialogContent>
        {deployType === ArtifactType.Config && (
          <>
            <Stack py={2} alignItems='flex-start' spacing={2}>
              <Typography>
                Based on the device group <strong>{group.name}</strong>, you may only deploy projects that
                share the same device family{' '}
                {group.deviceFamily ? (
                  <span>
                    of <strong>{group.deviceFamily}</strong>
                  </span>
                ) : (
                  ''
                )}
              </Typography>
              {controllerBuildEnabled && onCreateNewProject && (
                <Button color='primary' size='medium' variant='outlined' onClick={onCreateNewProject}>
                  Create new project
                </Button>
              )}
            </Stack>
            <Divider />
          </>
        )}

        <Stack py={2} spacing={2}>
          <Typography fontWeight={600}>
            Deploy an existing {DEPLOY_TYPE_MAP.get(deployType)}{' '}
            {uploadBusy && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          <Stack spacing={3}>
            <Stack direction='row' gap={1} alignItems='flex-end' width='100%'>
              <Box flex='2 2 auto'>
                <ControllerAutocomplete<string>
                  AutocompleteProps={{
                    id: 'artifact-name',
                    value: artifactName,
                    options: nameOptions.map((name) => name),
                    onChange: handleArtifactNameChange,
                    disabled: deployType === ArtifactType.Ota || uploadBusy,
                  }}
                  label={`Target ${DEPLOY_TYPE_MAP.get(deployType)}`}
                  placeholder={
                    nameOptions.length ? 'Select a name' : `No ${DEPLOY_TYPE_MAP.get(deployType)} options`
                  }
                  autoFocus
                  required
                  fullWidth
                />
              </Box>
              {deployType === ArtifactType.Apk && (
                <Box flex='0 0 120px' height='100%' display='flex' flexDirection='column' alignItems='center'>
                  <Typography
                    sx={{
                      color: 'secondary.main',
                      fontSize: '12px',
                      fontWeight: 400,
                    }}
                  >
                    .apk file type
                  </Typography>
                  <FileUploadButton
                    accept={['.apk']}
                    assetType='apk'
                    buttonText='New Apk'
                    onFileSelected={handleArtifactUpload}
                    onFileRejected={handleArtifactUploadError}
                    onFileUploadBusy={handleArtifactUploadBusy}
                  />
                </Box>
              )}
            </Stack>
            <ControllerSelect
              label='Version'
              placeholder={
                !artifactName
                  ? 'Select name first'
                  : versionOptions.length
                  ? 'Select a version'
                  : 'No version options'
              }
              autoFocus
              required
              fullWidth
              value={artifactVersion || ''}
              onChange={handleArtifactVersionChange}
              disabled={!versionOptions.length || uploadBusy}
            >
              {deployType === ArtifactType.Ota
                ? versionOptions
                    .reduce<string[]>((acc, cur, idx, arr) => {
                      const currMajVer = cur.split('.')[0];
                      const prevMajVer = (arr[idx - 1] || '').split('.')[0];
                      return currMajVer !== prevMajVer
                        ? [...acc, `Mason OS ${currMajVer}.x`, cur]
                        : [...acc, cur];
                    }, [])
                    .map((ver) => (
                      <ControllerMenuItem key={ver} disabled={ver.startsWith('Mason OS')} value={ver}>
                        {ver}
                      </ControllerMenuItem>
                    ))
                : versionOptions.map((ver) => (
                    <ControllerMenuItem key={ver} value={ver}>
                      {ver}
                    </ControllerMenuItem>
                  ))}
            </ControllerSelect>
            {!deployBusy && deployError && (
              <Alert severity='error' variant='outlined' sx={{ width: '100%' }}>
                There was a problem issuing the deployment
              </Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleDeployArtifact}
          disabled={!isValidDeploy || uploadBusy}
        >
          Deploy {DEPLOY_TYPE_MAP.get(deployType)}
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}

const getOsFromApiLevel = (apiLevel?: number) => {
  switch (apiLevel) {
    case 23:
      return '1.';
    case 25:
      return '2.';
    case 27:
      return '3.';
    case 30:
      return '6.';
  }
};
